import React, { useEffect, useState } from "react";
import axios from "axios";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useApp, API } from "@/contexts/AppContext";
import LangSwitcher from "@/components/LangSwitcher";
import {
  HeartPulse, LayoutDashboard, Users, Calendar, Megaphone, BookOpen,
  MessageSquare, LogOut, UsersRound, ScrollText, Building2, BedDouble, Cross, CreditCard, Sunrise,
  Siren, Baby, Syringe, Pill, AlertTriangle, Database,
} from "lucide-react";

export default function DashboardLayout() {
  const { t, user, logout } = useApp();
  const navigate = useNavigate();
  const [hospital, setHospital] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hospitals/me`).then((r) => setHospital(r.data)).catch(() => {});
  }, []);

  const role = user?.role;
  const isAdmin = role === "hospital_admin";
  const isDoctor = role === "doctor";
  const isNurse = role === "nurse";
  const isCaregiver = role === "caregiver";
  const clinical = isAdmin || isDoctor || isNurse || role === "secretary";

  const links = [
    { to: "/dashboard", end: true, label: t.dash.home, icon: LayoutDashboard, testid: "nav-home", show: true },
    { to: "/dashboard/my-day", label: t.dash.my_day, icon: Sunrise, testid: "nav-my-day", show: isDoctor || isAdmin },
    { to: "/dashboard/patients", label: t.dash.patients, icon: Users, testid: "nav-patients", show: true },
    { to: "/dashboard/hospitalizations", label: t.dash.hospitalizations, icon: BedDouble, testid: "nav-hospitalizations", show: true },
    { to: "/dashboard/deaths", label: t.dash.deaths, icon: Cross, testid: "nav-deaths", show: isAdmin || isDoctor || isNurse },
    { to: "/dashboard/appointments", label: t.dash.appointments, icon: Calendar, testid: "nav-appointments", show: true },
    { section: t.dash.services, show: clinical },
    { to: "/dashboard/services/urgences", label: t.dash.emergency, icon: Siren, testid: "nav-urgences", show: clinical },
    { to: "/dashboard/services/maternite", label: t.dash.maternity, icon: Baby, testid: "nav-maternite", show: clinical },
    { to: "/dashboard/services/pediatrie", label: t.dash.pediatrics, icon: Syringe, testid: "nav-pediatrie", show: clinical },
    { to: "/dashboard/services/pharmacie", label: t.dash.pharmacy, icon: Pill, testid: "nav-pharmacie", show: clinical },
    { to: "/dashboard/epidemic-alerts", label: t.dash.epidemic, icon: AlertTriangle, testid: "nav-epidemic", show: isAdmin || isDoctor || isNurse },
    { to: "/dashboard/dhis2", label: t.dash.dhis2, icon: Database, testid: "nav-dhis2", show: clinical },
    { section: "Outils", show: true },
    { to: "/dashboard/advice", label: t.dash.advice, icon: BookOpen, testid: "nav-advice", show: true },
    { to: "/dashboard/sms", label: t.dash.sms, icon: MessageSquare, testid: "nav-sms", show: true },
    { to: "/dashboard/team", label: t.dash.team, icon: UsersRound, testid: "nav-team", show: isAdmin },
    { to: "/dashboard/subscribe", label: t.dash.subscribe, icon: CreditCard, testid: "nav-subscribe", show: isAdmin },
    { to: "/dashboard/audit", label: t.dash.audit, icon: ScrollText, testid: "nav-audit", show: isAdmin },
  ].filter((l) => l.show && !(isCaregiver && l.section));

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      <aside className="w-64 bg-white border-r border-[#E5EBE7] flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-[#E5EBE7] flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#1E4631] grid place-items-center">
            <HeartPulse className="w-5 h-5 text-white"/>
          </div>
          <span className="font-[Outfit] font-bold text-lg">KWIKILA</span>
        </div>
        {hospital && (
          <div className="px-4 py-3 mx-3 mt-3 bg-[#1E4631]/5 rounded-lg border border-[#1E4631]/10" data-testid="hospital-banner">
            <div className="flex items-center gap-2 text-xs text-[#5C6B61] uppercase tracking-wide">
              <Building2 className="w-3 h-3"/> {t.dash.hospital}
            </div>
            <div className="mt-0.5 font-[Outfit] font-semibold text-sm text-[#1E4631] truncate">{hospital.name}</div>
            <div className="text-xs text-[#5C6B61]">{hospital.city}</div>
          </div>
        )}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {links.map((l, i) => l.section ? (
            <div key={`s-${i}`} className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-[#8A9990] font-semibold">{l.section}</div>
          ) : (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                  isActive ? "bg-[#1E4631] text-white" : "text-[#5C6B61] hover:bg-[#FDFBF7] hover:text-[#1E4631]"
                }`}>
              <l.icon className="w-4 h-4"/> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5EBE7] space-y-3">
          <div className="text-xs">
            <div className="font-semibold text-[#1A1D1A] truncate">{user?.name}</div>
            <div className="truncate text-[#5C6B61]">{user?.email}</div>
            <div className="mt-1 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D96C4A]/10 text-[#D96C4A] font-bold">
              {t.roles[role] || role}
            </div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#E5EBE7] hover:bg-[#FDFBF7] text-sm font-medium text-[#5C6B61] hover:text-[#C62828] transition">
            <LogOut className="w-4 h-4"/> {t.dash.logout}
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-[#E5EBE7] sticky top-0 z-30">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="text-sm text-[#5C6B61]">
              <span className="font-semibold text-[#1A1D1A]">{t.dash.welcome}</span>, {user?.name}
            </div>
            <LangSwitcher/>
          </div>
        </header>
        <main className="flex-1 p-8 overflow-x-auto"><Outlet/></main>
      </div>
    </div>
  );
}
